import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, Loader2, AlertCircle } from "lucide-react";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/**
 * Pay-now button for an accepted delivery that is in awaiting_payment.
 * - Reads /payments/config to confirm Razorpay is wired on the server
 * - Creates an order (idempotent) via /payments/razorpay/order
 * - Opens the standard Razorpay Checkout
 * - On success, verifies the signature server-side via /payments/razorpay/verify
 *   (the webhook is still source-of-truth, but client-verify gives instant UX)
 */
const RazorpayPay = ({ delivery, user, onPaid, autoOpen = false }) => {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => setConfig({ configured: false }));
  }, []);

  const openCheckout = async () => {
    if (!config?.configured) {
      toast.error("Online payment isn't live yet. Please contact support or ask the carrier to accept COD.");
      return;
    }
    setBusy(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Razorpay failed to load");
      const r = await api.post("/payments/razorpay/order", { delivery_id: delivery.id });
      const options = {
        key: r.data.key_id,
        amount: r.data.amount,
        currency: r.data.currency,
        order_id: r.data.order_id,
        name: "CarryIt",
        description: `Parcel ${delivery.parcel_id?.slice(0, 8) || ""}`,
        prefill: {
          name: user?.name || "",
          contact: user?.phone || "",
        },
        theme: { color: "#D38138" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment confirmed. Carrier has been notified.");
            onPaid?.();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment couldn't be verified. If money was deducted, we'll reconcile automatically.");
          }
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. You can try again.");
      });
      rzp.open();
      setOpened(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't start checkout");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (autoOpen && config?.configured && !opened && !busy) {
      openCheckout();
    }
    // eslint-disable-next-line
  }, [autoOpen, config?.configured]);

  if (config && !config.configured) {
    return (
      <div data-testid="razorpay-not-configured" className="w-full rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-xs text-amber-900">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
        <div>
          Online payment isn't live on this server yet. Please contact support or ask your carrier to switch to Cash on Delivery.
        </div>
      </div>
    );
  }

  return (
    <Button
      data-testid="razorpay-pay-btn"
      onClick={openCheckout}
      disabled={busy || !config?.configured}
      className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
    >
      {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <CreditCard className="w-4 h-4 mr-1.5"/>}
      Pay ₹{Number(delivery.sender_total || 0).toFixed(0)}
    </Button>
  );
};

export default RazorpayPay;
